import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardLayout from "@/pages/DashboardLayout";
import DashboardHome from "@/pages/DashboardHome";
import ProjectsPage from "@/pages/ProjectsPage";
import AchievementsPage from "@/pages/AchievementsPage";
import SettingsPage from "@/pages/SettingsPage";
import PublicProfilePage from "@/pages/PublicProfilePage";
import GitHubCallbackPage from "@/pages/GitHubCallbackPage";
import HackathonsPage from "@/pages/HackathonsPage";
import HackathonExplore from "@/pages/HackathonExplore";
import HallOfFamePage from "@/pages/HallOfFamePage";
import PublicHackathonPage from "@/pages/PublicHackathonPage";
import AdminPage from "@/pages/AdminPage";

// Components
import { GrainOverlay } from "@/components/GrainOverlay";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/profile/:slug" element={<PublicProfilePage />} />
      <Route path="/hackathons" element={<HallOfFamePage />} />
      <Route path="/hackathons/:id" element={<PublicHackathonPage />} />
      <Route path="/hall-of-fame" element={<HallOfFamePage />} />
      <Route path="/auth/github/callback" element={<GitHubCallbackPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardHome />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="achievements" element={<AchievementsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="hackathons" element={<HackathonsPage />} />
        <Route path="explore" element={<HackathonExplore />} />
        <Route path="admin" element={<AdminPage />} />
      </Route>
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="app-container relative">
          <GrainOverlay />
          <AppRoutes />
          <Toaster position="bottom-right" theme="dark" />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
